import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { generalDataSchema } from '../utils/validation';
import apiClient from '../utils/apiClient';
import { DataSummary } from './DataSummary';
import FileUpload from './FileUpload';
import { Upload, FileText, Calendar, Building, CreditCard, FileCheck, FileSpreadsheet, HelpCircle, Check, X, FileUp } from 'lucide-react';
import { crearDepurador } from '../utils/depurador';

// Configurar apiClient para formularios
apiClient.defaults.headers.common[ 'Content-Type' ] = 'multipart/form-data';
apiClient.defaults.withCredentials = true;

// Crear un depurador específico para el módulo de FormPage
const depurador = crearDepurador({ modulo: 'FormPage' });

const FormPage = () =>
{
  const [ csvData, setCsvData ] = useState<any[]>([]);
  const [ csvFile, setCsvFile ] = useState<File | null>(null);
  const [ previewData, setPreviewData ] = useState<any>(null);
  const [ isSubmitting, setIsSubmitting ] = useState(false);
  const [ uploadSuccess, setUploadSuccess ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  const [ showFormatModal, setShowFormatModal ] = useState(false);
  const [ formatContent, setFormatContent ] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    resolver: zodResolver(generalDataSchema),
    defaultValues: {
      orgNom: '',
      orgCuit: undefined,
      periodoAnio: new Date().getFullYear(),
      periodoMes: new Date().getMonth() + 1,
      tipoCod: 111,
      titulo: 'DDJJ Jubilaciones',
      transferidas: false,
    }
  });

  const showReview = watch('showReview', false);

  const onSubmit = async (data: any) =>
  {
    // Validación de archivo
    if (!csvFile)
    {
      setError('Por favor seleccione un archivo para continuar');
      return;
    }

    // Validación de formato de archivo
    const validFormats = [ 'text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ];
    const fileExtension = csvFile.name.split('.').pop()?.toLowerCase();

    if (!validFormats.includes(csvFile.type))
    {
      setError(`El formato del archivo no es válido. Formatos aceptados: CSV o Excel. Usted subió un archivo ${fileExtension?.toUpperCase() || 'desconocido'}.`);
      return;
    }

    // Sanitizar inputs
    const sanitizedData = {
      ...data,
      orgNom: data.orgNom.replace(/[^a-zA-Z0-9\s]/g, ''),
      titulo: data.titulo ? data.titulo.replace(/[^a-zA-Z0-9\s]/g, '') : null,
      transferidas: data.transferidas ? 1 : 0,
    };

    setIsSubmitting(true);
    setError(null);

    try
    {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('data', JSON.stringify(sanitizedData));

      const response = await apiClient.post('/api/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response.data.success)
      {
        try
        {
          const jsonData = JSON.parse(response.data.data);
          if (!jsonData.aportes || !Array.isArray(jsonData.aportes))
          {
            throw new Error('Estructura de datos inválida del servidor');
          }
          setCsvData(jsonData.aportes);
          setPreviewData(data);
          setUploadSuccess(true);
        } catch (parseError)
        {
          depurador.error('Error al analizar la respuesta del servidor:', parseError);
          setError('Error en el formato de los datos recibidos. Contacte al soporte técnico.');
          setUploadSuccess(false);
        }
      }
    } catch (error: any)
    {
      depurador.grupo('Error al subir el archivo');
      depurador.error('Error completo:', error);
      depurador.error('Respuesta:', error.response);
      depurador.finGrupo();
      // Mejorar mensajes de error específicos
      if (error.response?.status === 400)
      {
        if (error.response?.data?.error?.includes('formato'))
        {
          setError(`Error de formato: ${error.response.data.error}. El servidor actualmente solo acepta archivos CSV.`);
        } else
        {
          const errorMsg = error.response.data.error || 'Error en la validación de datos.';
          const detailsMsg = error.response.data.details ? `\nDetalles: ${error.response.data.details}` : '';
          setError(`${errorMsg}${detailsMsg} Por favor, revise la información ingresada.`);

          // Mostrar alerta con los campos requeridos faltantes
          if (error.response.data.details)
          {
            alert(`Campos requeridos faltantes:\n${error.response.data.details}`);
          }
        }
      } else if (error.response?.status === 415)
      {
        setError('El formato del archivo no es compatible con el servidor. Por favor, utilice un archivo CSV.');
      } else
      {
        setError('Error al procesar el archivo. Por favor, inténtelo de nuevo o contacte al soporte técnico.');
      }
    } finally
    {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    if (e.target.files && e.target.files.length > 0)
    {
      setCsvFile(e.target.files[ 0 ]);
      setUploadSuccess(false);
      setPreviewData(null);
    }
  };

  const handleConfirm = async () =>
  {
    if (!csvData.length || !previewData)
    {
      setError('No hay datos para generar la declaración jurada.');
      return;
    }

    if (!csvFile)
    {
      setError('No se encontró el archivo original. Por favor, vuelva a cargar el archivo.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try
    {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('data', JSON.stringify({
        ...previewData,
        orgNom: previewData.orgNom.replace(/[^a-zA-Z0-9\s]/g, ''),
        titulo: previewData.titulo ? previewData.titulo.replace(/[^a-zA-Z0-9\s]/g, '') : null,
        transferidas: previewData.transferidas ? 1 : 0,
      }));
      formData.append('confirmed', 'true');

      // Resetear el formulario después de la descarga
      reset();
      setCsvFile(null);
      setCsvData([]);
      setPreviewData(null);
      setUploadSuccess(false);

      const response = await apiClient.post('/api/upload/confirm', formData, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Verificar si la respuesta es válida
      if (response.status !== 200 || response.data.size === 0)
      {
        throw new Error('La respuesta del servidor no contiene datos válidos');
      }

      // Crear un objeto URL para el blob
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'importa_dat.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Limpiar el estado después de una descarga exitosa
      setCsvFile(null);
      setCsvData([]);
      setPreviewData(null);
      setUploadSuccess(false);
    } catch (error: any)
    {
      depurador.grupo('Error al generar el archivo ZIP');
      depurador.error('Error completo:', error);
      depurador.error('Respuesta:', error.response);
      depurador.finGrupo();

      if (error.response)
      {
        if (error.response.status === 400)
        {
          setError('El servidor no pudo procesar los datos. Verifique que el formato del archivo sea correcto (CSV).');
        } else if (error.response.status === 415)
        {
          setError('El formato del archivo no es compatible con el servidor. Por favor, utilice un archivo CSV.');
        } else if (error.response.status === 500)
        {
          setError('Error interno del servidor. Por favor, contacte al soporte técnico.');
        } else
        {
          setError(`Error al generar el archivo ZIP (${error.response.status}). Por favor, inténtelo de nuevo.`);
        }
      } else if (error.request)
      {
        setError('No se pudo conectar con el servidor. Verifique su conexión a internet.');
      } else
      {
        setError(`Error al generar el archivo ZIP: ${error.message}. Por favor, inténtelo de nuevo.`);
      }
    } finally
    {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () =>
  {
    setCsvFile(null);
    setCsvData([]);
    setPreviewData(null);
    setUploadSuccess(false);
  };

  useEffect(() => {
    fetch('/formato.md')
      .then(response => response.text())
      .then(text => setFormatContent(text))
      .catch(error => depurador.error('Error al cargar el formato:', error));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8" >
      {showFormatModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowFormatModal(false)}>
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-4xl max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Formato de carga de datos</h3>
              <button onClick={() => setShowFormatModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <Markdown remarkPlugins={[remarkGfm]}>{formatContent}</Markdown>
          </div>
        </div>
      )}
      <div className="max-w-4xl mx-auto" >
        <div className="text-center mb-12" >
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl" >
            Generador de Declaraciones Juradas
          </h1>
          <p className="mt-3 text-lg text-gray-500" >
            Complete el formulario y suba su archivo CSV para generar la declaración jurada.
          </p>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-lg" >
          <div className="px-4 py-5 sm:px-6 bg-blue-50" >
            <h2 className="text-lg leading-6 font-medium text-gray-900 flex items-center" >
              <FileText className="mr-2 h-5 w-5 text-blue-500" />
              Información General
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" >
              Datos del organismo y período de la declaración.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="divide-y divide-gray-200" >
            <div className="px-4 py-5 sm:p-6" >
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" >
                <div className="sm:col-span-3" >
                  <label htmlFor="orgNom" className="block text-sm font-medium text-gray-700" >
                    Nombre del Organismo
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm" >
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" >
                      <Building className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="orgNom"
                      className={`block w-full pl-10 sm:text-sm rounded-md ${errors.orgNom ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`
                      }
                      placeholder="Ej: Escuela Nº 123"
                      {...register('orgNom')}
                    />
                  </div>
                  {
                    errors.orgNom && (
                      <p className="mt-2 text-sm text-red-600" > {errors.orgNom.message as string} </p>
                    )
                  }
                </div>

                <div className="sm:col-span-3" >
                  <label htmlFor="orgCuit" className="block text-sm font-medium text-gray-700" >
                    CUIT del Organismo
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm" >
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" >
                      <CreditCard className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      id="orgCuit"
                      className={`block w-full pl-10 sm:text-sm rounded-md ${errors.orgCuit ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                      placeholder="Ej: 30123456789"
                      {...register('orgCuit', { valueAsNumber: true })}
                    />
                  </div>
                  {
                    errors.orgCuit && (
                      <p className="mt-2 text-sm text-red-600" > {errors.orgCuit.message as string} </p>
                    )
                  }
                </div>

                <div className="sm:col-span-2" >
                  <label htmlFor="periodoAnio" className="block text-sm font-medium text-gray-700" >
                    Año
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm" >
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" >
                      <Calendar className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      id="periodoAnio"
                      className={`block w-full pl-10 sm:text-sm rounded-md ${errors.periodoAnio ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                      placeholder="Ej: 2024"
                      {...register('periodoAnio', { valueAsNumber: true })}
                    />
                  </div>
                  {
                    errors.periodoAnio && (
                      <p className="mt-2 text-sm text-red-600" > {errors.periodoAnio.message as string} </p>
                    )
                  }
                </div>

                <div className="sm:col-span-2" >
                  <label htmlFor="periodoMes" className="block text-sm font-medium text-gray-700" >
                    Mes
                  </label>
                  <div className="mt-1" >
                    <select
                      id="periodoMes"
                      className={`block w-full sm:text-sm rounded-md ${errors.periodoMes ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                      {...register('periodoMes', { valueAsNumber: true })}
                    >
                      <option value="1" > Enero </option>
                      <option value="2" > Febrero </option>
                      <option value="3" > Marzo </option>
                      <option value="4" > Abril </option>
                      <option value="5" > Mayo </option>
                      <option value="6" > Junio </option>
                      <option value="7" > Julio </option>
                      <option value="8" > Agosto </option>
                      <option value="9" > Septiembre </option>
                      <option value="10" > Octubre </option>
                      <option value="11" > Noviembre </option>
                      <option value="12" > Diciembre </option>
                    </select>
                  </div>
                  {
                    errors.periodoMes && (
                      <p className="mt-2 text-sm text-red-600" > {errors.periodoMes.message as string} </p>
                    )
                  }
                </div>

                <div className="sm:col-span-2" >
                  <label htmlFor="tipoCod" className="block text-sm font-medium text-gray-700" >
                    Código de Tipo
                  </label>
                  <div className="mt-1" >
                    <select
                      id="tipoCod"
                      className={`block w-full sm:text-sm rounded-md ${errors.tipoCod ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                      {...register('tipoCod', { valueAsNumber: true })}
                    >
                      <option value="111" > 111 - Original Normal </option>
                      <option value="112" > 112 - Correctiva Normal </option>
                      <option value="211" > 211 - Original SAC </option>
                      <option value="212" > 212 - Correctiva SAC </option>
                      <option value="301" > 301 - Complementaria 1 </option>
                      <option value="302" > 302 - Complementaria 2 </option>
                      <option value="303" > 303 - Complementaria 3 </option>
                      <option value="304" > 304 - Complementaria 4 </option>
                    </select>
                  </div>
                  {
                    errors.tipoCod && (
                      <p className="mt-2 text-sm text-red-600" > {errors.tipoCod.message as string} </p>
                    )
                  }
                </div>

                <div className="sm:col-span-6" >
                  <label htmlFor="titulo" className="block text-sm font-medium text-gray-700" >
                    Título(Opcional)
                  </label>
                  <div className="mt-1" >
                    <input
                      type="text"
                      id="titulo"
                      className="block w-full sm:text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Título de la declaración jurada"
                      {...register('titulo')}
                    />
                  </div>
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <div className="flex items-center mt-4">
                    <input
                      id="transferidas"
                      type="checkbox"
                      {...register('transferidas')}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <label htmlFor="transferidas" className="ml-2 block text-sm text-gray-900">
                      ¿Son cargos transferidos?
                    </label>
                  </div>
                </div>
                
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="tipoCargo" className="block text-sm font-medium text-gray-700">
                    Tipo de códigos de cargo en el archivo
                  </label>
                  <div className="mt-1">
                    <select
                      id="tipoCargo"
                      className="block w-full sm:text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      {...register('tipoCargo')}
                    >
                      <option value="sueldo">Cargos Sueldo (códigos privados)</option>
                      <option value="provincia">Cargos Provincia (códigos oficiales)</option>
                    </select>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Seleccione "Cargos Sueldo" si su archivo contiene códigos de cargos privados o "Cargos Provincia" si contiene códigos oficiales.
                  </p>
                </div>

              </div>
            </div>

            <div className="px-4 py-5 sm:p-6 bg-gray-50" >
              <div className="text-lg font-medium text-gray-900 mb-4 flex items-center justify-between" >
                <div className="flex items-center" >
                  <Upload className="mr-2 h-5 w-5 text-blue-500" />
                  Archivo XLS
                </div>
                <button 
                  onClick={() => setShowFormatModal(true)}
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                >
                  <FileText className="mr-1 h-4 w-4" />
                  Formato
                </button>
                {/* <a
                  href="/importa.csv"
                  download
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                >
                  <FileSpreadsheet className="mr-1 h-4 w-4" />
                  Descargar CSV ejemplo
                </a> */}
                <a
                  href="/importa_JUBI_ejemplo.xlsx"
                  download
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                >
                  <FileSpreadsheet className="mr-1 h-4 w-4" />
                  Descargar XLS ejemplo
                </a>
              </div>

              <div className="bg-blue-50 p-4 rounded-md mb-4 flex items-start" >
                <HelpCircle className="h-5 w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-blue-800" >
                  El archivo debe contener los datos de los empleados con los campos: <b />
                  tipoDoc, nroDoc, genero, cuil, fechaNac, apellidos, nombres, escalafon, agrup, cargo,hsCargo, claseCargo, porcentajeSub, fechaIngreso, revista, aporte, contribucion, remunerativo, no-remunerativo, asig-fliares, pjeAporte, pjeContrib, cantDiasPorpSac, amparo
                </p>
              </div>
              {error && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <X className="h-5 w-5 text-red-500" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Error al procesar la solicitud</h3>
                      <p className="mt-1 text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}
              <FileUpload onFileUpload={(file) =>
              {
                setCsvFile(file);
                setUploadSuccess(false);
                setPreviewData(null);
              }} />
            </div>

            <div className="px-4 py-3 bg-gray-50 text-right sm:px-6" >
              {!uploadSuccess && (
              <button
                type="submit"
                className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${isSubmitting ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Procesando...' : 'Generar DDJJ'}
              </button>
            )}
            </div>
          </form>
        </div>

        {
          uploadSuccess && (
            <div className="mt-8" >
              <div className="bg-white shadow overflow-hidden sm:rounded-lg" >
                <div className="px-4 py-5 sm:px-6 bg-yellow-50" >
                  <h3 className="text-lg leading-6 font-medium text-gray-900" >
                    Revisión de Datos
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500" >
                    Por favor, revise los datos antes de confirmar la generación del archivo.
                  </p>
                </div>
              </div>

              <DataSummary data={csvData} />

              <div className="mt-6 flex justify-end space-x-4" >
                <button
                  onClick={handleCancel}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  disabled={isSubmitting}
                >
                  <X className="mr-2 h-4 w-4" />
                  Cancelar
                </button>
                <button
                  onClick={handleConfirm}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  disabled={isSubmitting}
                >
                  {
                    isSubmitting ? (
                      'Generando ZIP...'
                    ) : (
                      <>
                        <Check className="mr-2 h-4 w-4" />
                        Confirmar y Descargar ZIP
                      </>
                    )
                  }
                </button>
              </div>
            </div>
          )
        }
      </div >
    </div >
  );
};

export default FormPage;